import { useMutation, useQueryClient } from 'react-query';
import { REPORTS_KEY } from '@/utils/hooks/reports';
import { updateReport } from '@/utils/api/reports.api';

const CONFLICT = 409;

/**
 * The report autosave mutation. A bespoke `useMutation` (not
 * `useOptimisticMutation`) because the autosave path must:
 *   - NOT invalidate-on-settled — a refetch after every save can land a stale
 *     server snapshot over newer local edits mid-burst;
 *   - NOT roll back on a 409 — that would silently discard the user's unsaved
 *     edits, which is the whole thing we're protecting;
 *   - NOT retry a 409 — it would just re-conflict with the same stale version.
 *
 * variables: { config, expectedVersion }
 * onVersionAdvanced(version): let the saver sync its versionRef synchronously so
 *   a trailing PUT carries the new expected_version.
 * onConflict({ version, config }): fire the conflict UX (current server state).
 */
const useSaveReportMutation = (
    reportId,
    { onVersionAdvanced, onConflict } = {},
) => {
    const qc = useQueryClient();
    const key = [REPORTS_KEY, reportId];

    return useMutation(
        ({ config, expectedVersion }) =>
            updateReport({ id: reportId, config, expectedVersion }),
        {
            onMutate: ({ config }) => {
                qc.cancelQueries(key);
                const prev = qc.getQueryData(key);
                const prevList = qc.getQueryData(REPORTS_KEY);
                const updated = new Date().toISOString();
                qc.setQueryData(key, (r) =>
                    r ? { ...r, config, updated } : r,
                );
                qc.setQueryData(REPORTS_KEY, (list = []) =>
                    list.map((r) =>
                        r.id === reportId ? { ...r, config, updated } : r,
                    ),
                );
                return { prev, prevList };
            },
            onSuccess: ({ version }) => {
                // Stamp the new (incremented) version into both caches.
                qc.setQueryData(key, (r) => (r ? { ...r, version } : r));
                qc.setQueryData(REPORTS_KEY, (list = []) =>
                    list.map((r) =>
                        r.id === reportId ? { ...r, version } : r,
                    ),
                );
                onVersionAdvanced?.(version);
            },
            onError: (err, _vars, ctx) => {
                if (err?.status === CONFLICT) {
                    // Keep the user's optimistic edits in cache and surface the
                    // conflict — do not roll back to server state.
                    onConflict?.(err.body);
                    return;
                }
                if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev);
                if (ctx?.prevList !== undefined) {
                    qc.setQueryData(REPORTS_KEY, ctx.prevList);
                }
            },
            retry: false,
        },
    );
};

export default useSaveReportMutation;
